import React, { useState, useEffect } from 'react'
import { useAuth } from '../../context/AuthContext'
import { supabase } from '../../config/supabase'
import { useNavigate } from 'react-router-dom'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import { motion } from 'framer-motion'

const { FiBook, FiCalendar, FiTrendingUp, FiStar } = FiIcons

const StudentDashboard = () => {
  const { userProfile } = useAuth()
  const navigate = useNavigate()
  const [selectedDay, setSelectedDay] = useState(1)
  const [todayLessons, setTodayLessons] = useState([])
  const [progress, setProgress] = useState({ completed: 0, total: 30 })
  const [loading, setLoading] = useState(true)

  const [assignedCurriculumIds, setAssignedCurriculumIds] = useState([])
  const [maxDays, setMaxDays] = useState(30)
  const [assignmentsLoading, setAssignmentsLoading] = useState(true)

  useEffect(() => {
    if (userProfile) {
      fetchAssignedCurricula()
    } else {
      setAssignmentsLoading(false)
    }
  }, [userProfile])

  useEffect(() => {
    if (maxDays > 0 && selectedDay > maxDays) {
      setSelectedDay(maxDays)
    }
  }, [maxDays, selectedDay])

  useEffect(() => {
    if (userProfile && assignedCurriculumIds.length > 0) {
      fetchProgress()
      fetchTodayLessons()
    } else if (userProfile && assignedCurriculumIds.length === 0) {
      setTodayLessons([])
      setProgress({ completed: 0, total: 0 })
    }
  }, [userProfile, selectedDay, assignedCurriculumIds])

  const fetchAssignedCurricula = async () => {
    setAssignmentsLoading(true)
    try {
      const { data, error } = await supabase
        .from('curriculum_student_assignments')
        .select('curriculum_id, curricula(total_days)')
        .eq('student_id', userProfile.user_id)

      if (error) throw error
      const list = data || []
      setAssignedCurriculumIds(list.map((a) => a.curriculum_id))
      const totalDaysList = list
        .map((a) => a.curricula?.total_days)
        .filter((n) => n != null && Number(n) > 0)
      const computedMax = totalDaysList.length > 0 ? Math.max(...totalDaysList.map(Number)) : 30
      setMaxDays(Math.min(365, Math.max(1, computedMax)))
    } catch (error) {
      console.error('Error fetching assigned curricula:', error)
      setAssignedCurriculumIds([])
      setMaxDays(30)
    } finally {
      setAssignmentsLoading(false)
    }
  }

  const fetchProgress = async () => {
    if (assignedCurriculumIds.length === 0) return
    try {
      const { data: courseIds } = await supabase
        .from('courses')
        .select('id')
        .in('curriculum_id', assignedCurriculumIds)

      const ids = (courseIds || []).map((c) => c.id)
      if (ids.length === 0) {
        setProgress({ completed: 0, total: 0 })
        return
      }

      const { data: completedLessons, error } = await supabase
        .from('lesson_progress')
        .select('lesson_id')
        .eq('student_id', userProfile.user_id)
        .eq('completed', true)

      if (error) throw error

      const { data: allAssignedLessons } = await supabase
        .from('lessons')
        .select('id, day_number')
        .in('course_id', ids)

      const total = allAssignedLessons?.length || 0
      const completedIds = completedLessons?.map((cl) => cl.lesson_id) || []
      const completed = completedIds.filter((id) =>
        allAssignedLessons?.some((l) => l.id === id)
      ).length

      setProgress({ completed, total })

      if (allAssignedLessons?.length) {
        const dayNumbers = [...new Set(allAssignedLessons.map((l) => l.day_number))].sort((a, b) => a - b)
        for (const day of dayNumbers) {
          const dayLessons = allAssignedLessons.filter((l) => l.day_number === day)
          const hasIncomplete = dayLessons.some((l) => !completedIds.includes(l.id))
          if (hasIncomplete) {
            setSelectedDay(day)
            break
          }
        }
      }
    } catch (error) {
      console.error('Error fetching progress:', error)
    }
  }

  const fetchTodayLessons = async () => {
    if (assignedCurriculumIds.length === 0) return
    try {
      setLoading(true)

      const { data: courseIds } = await supabase
        .from('courses')
        .select('id')
        .in('curriculum_id', assignedCurriculumIds)

      const ids = (courseIds || []).map((c) => c.id)
      if (ids.length === 0) {
        setTodayLessons([])
        return
      }

      const { data: lessons, error } = await supabase
        .from('lessons')
        .select(`
          *,
          lesson_progress (
            completed,
            completed_at
          )
        `)
        .in('course_id', ids)
        .eq('day_number', selectedDay)
        .order('order_index')

      if (error) throw error

      const lessonsWithProgress = (lessons || []).map((lesson) => ({
        ...lesson,
        completed: lesson.lesson_progress?.[0]?.completed || false,
        completed_at: lesson.lesson_progress?.[0]?.completed_at
      }))

      setTodayLessons(lessonsWithProgress)
    } catch (error) {
      console.error('Error fetching lessons:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleLessonClick = (lessonId) => {
    navigate(`/lesson/${lessonId}`)
  }

  const completedToday = todayLessons.filter(lesson => lesson.completed).length
  const totalToday = todayLessons.length
  const dayProgress = totalToday > 0 ? (completedToday / totalToday) * 100 : 0
  const hasAssignments = assignedCurriculumIds.length > 0

  if (assignmentsLoading) {
    return (
      <div className="max-w-6xl mx-auto flex items-center justify-center min-h-[400px]">
        <div className="w-12 h-12 rounded-full border-4 border-[#00AAFF]/30 border-t-[#00AAFF] animate-spin" />
      </div>
    )
  }

  if (userProfile && !hasAssignments) {
    return (
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-[#00AAFF] to-[#1B4C87] rounded-2xl p-8 text-white mb-8"
        >
          <h1 className="text-3xl font-bold mb-2">Welcome back, {userProfile?.first_name}! 🌟</h1>
          <p className="text-blue-100 text-lg">Ready to start learning</p>
        </motion.div>
        <div className="bg-white rounded-xl shadow-lg p-12 text-center">
          <SafeIcon icon={FiBook} className="w-20 h-20 mx-auto text-gray-300 mb-4" />
          <h2 className="text-xl font-bold text-gray-800 mb-2">No curriculum assigned yet</h2>
          <p className="text-gray-600 max-w-md mx-auto">
            Ask your parent to assign a curriculum to you from the Curricula page. Once assigned, your lessons will appear here.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Welcome Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-[#00AAFF] to-[#1B4C87] rounded-2xl p-8 text-white mb-8"
      >
        <h1 className="text-3xl font-bold mb-2">
          Welcome back, {userProfile?.first_name}! 🌟
        </h1>
        <p className="text-blue-100 text-lg">
          Ready to continue your learning journey? You're on day {selectedDay}!
        </p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Day Selector */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-800 flex items-center">
                <SafeIcon icon={FiCalendar} className="w-6 h-6 mr-2 text-[#00AAFF]" />
                Select Day
              </h2>
            </div>
            
            <div className="flex items-center gap-4">
              <label className="text-gray-600 font-medium">Day:</label>
              <select
                value={selectedDay}
                onChange={(e) => setSelectedDay(parseInt(e.target.value))}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00AAFF] focus:border-transparent"
              >
                {Array.from({ length: maxDays }, (_, i) => i + 1).map((day) => (
                  <option key={day} value={day}>
                    Day {day}
                  </option>
                ))}
              </select>
              
              {dayProgress === 100 && (
                <span className="flex items-center text-green-600 font-medium">
                  <SafeIcon icon={FiStar} className="w-4 h-4 mr-1" />
                  Completed!
                </span>
              )}
            </div>

            {/* Day Progress Bar */}
            <div className="mt-4">
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>Progress for Day {selectedDay}</span>
                <span>{completedToday}/{totalToday} lessons</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <motion.div
                  className="bg-gradient-to-r from-[#00AAFF] to-[#1B4C87] h-3 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${dayProgress}%` }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                />
              </div>
            </div>
          </motion.div>

          {/* Today's Classes */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
              <SafeIcon icon={FiBook} className="w-6 h-6 mr-2 text-[#00AAFF]" />
              Day {selectedDay} Classes
            </h2>

            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="animate-pulse bg-gray-200 h-20 rounded-lg" />
                ))}
              </div>
            ) : todayLessons.length > 0 ? (
              <div className="space-y-4">
                {todayLessons.map((lesson, index) => (
                  <motion.div
                    key={lesson.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + (index * 0.1) }}
                    onClick={() => handleLessonClick(lesson.id)}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      lesson.completed
                        ? 'bg-green-50 border-green-200 hover:bg-green-100'
                        : 'bg-gray-50 border-gray-200 hover:bg-gray-100 hover:border-[#00AAFF]'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-800">{lesson.title}</h3>
                        <p className="text-gray-600 text-sm mt-1">{lesson.subject}</p>
                      </div>
                      <div className="flex items-center">
                        {lesson.completed ? (
                          <div className="flex items-center text-green-600">
                            <SafeIcon icon={FiStar} className="w-5 h-5 mr-1" />
                            <span className="text-sm font-medium">Completed</span>
                          </div>
                        ) : (
                          <div className="px-3 py-1 bg-[#00AAFF] text-white text-sm rounded-full">
                            Start
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <SafeIcon icon={FiBook} className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">No lessons available for this day yet.</p>
              </div>
            )}
          </motion.div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Overall Progress */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
              <SafeIcon icon={FiTrendingUp} className="w-5 h-5 mr-2 text-[#00AAFF]" />
              Overall Progress
            </h3>
            
            <div className="text-center">
              <div className="relative inline-flex items-center justify-center w-24 h-24 mb-4">
                <svg className="w-24 h-24 transform -rotate-90" viewBox="0 0 36 36">
                  <path
                    className="text-gray-200"
                    stroke="currentColor"
                    strokeWidth="3"
                    fill="transparent"
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                  <path
                    className="text-[#00AAFF]"
                    stroke="currentColor"
                    strokeWidth="3"
                    fill="transparent"
                    strokeDasharray={`${progress.total > 0 ? (progress.completed / progress.total) * 100 : 0}, 100`}
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-xl font-bold text-gray-800">
                    {progress.total > 0 ? Math.round((progress.completed / progress.total) * 100) : 0}%
                  </span>
                </div>
              </div>
              
              <p className="text-gray-600">
                {progress.completed} of {progress.total} lessons completed
              </p>
            </div>
          </motion.div>

          {/* Motivational Card */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-gradient-to-br from-[#00AAFF]/10 to-[#1B4C87]/10 rounded-xl p-6"
          >
            <h3 className="text-lg font-bold text-[#1B4C87] mb-2" style={{ fontFamily: 'Dancing Script' }}>
              Keep Going! 🚀
            </h3>
            <p className="text-gray-700 text-sm">
              You're doing amazing! Every lesson brings you closer to your goals. 
              Remember, learning is a journey, not a destination.
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  )
}

export default StudentDashboard